/**
 * LeaderboardPage - User Rankings
 * Display top reviewers, sellers, and contributors
 */

import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import {
  Trophy,
  Medal,
  Star,
  TrendingUp,
  MessageSquare,
  Download,
  Upload,
  Users,
  Crown,
  ChevronLeft,
  Loader2,
  User,
  ShoppingBag,
  Award,
  Sparkles,
  ArrowUp,
  ArrowDown,
  Minus,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Leaderboard types configuration
const LEADERBOARD_TYPES = {
  reviewers: {
    title: 'Top Reviewers',
    subtitle: 'Most helpful community members',
    icon: MessageSquare,
    color: 'emerald',
    gradient: 'from-emerald-500 to-teal-500',
    stats: [
      { key: 'review_count', label: 'Reviews', icon: MessageSquare },
      { key: 'total_helpful', label: 'Helpful', icon: Star },
      { key: 'avg_rating_given', label: 'Avg Rating', icon: Star },
    ],
  },
  sellers: {
    title: 'Top Sellers',
    subtitle: 'Best-selling preset creators',
    icon: ShoppingBag,
    color: 'violet',
    gradient: 'from-violet-500 to-purple-500',
    stats: [
      { key: 'preset_count', label: 'Presets', icon: Upload },
      { key: 'total_downloads', label: 'Downloads', icon: Download },
      { key: 'avg_rating', label: 'Avg Rating', icon: Star },
    ],
  },
  contributors: {
    title: 'Top Contributors',
    subtitle: 'Most active community contributors',
    icon: Upload,
    color: 'amber',
    gradient: 'from-amber-500 to-orange-500',
    stats: [
      { key: 'presets_shared', label: 'Shared', icon: Upload },
      { key: 'total_downloads', label: 'Downloads', icon: Download },
      { key: 'avg_rating', label: 'Avg Rating', icon: Star },
    ],
  },
};

// Rank badge component
const RankBadge = ({ rank, size = 'md' }) => {
  const sizeClasses = {
    sm: 'w-6 h-6 text-xs',
    md: 'w-10 h-10 text-sm',
    lg: 'w-14 h-14 text-lg',
  };

  if (rank === 1) {
    return (
      <div className={`${sizeClasses[size]} rounded-full bg-gradient-to-br from-amber-400 to-yellow-500 flex items-center justify-center font-bold text-black shadow-lg shadow-amber-500/30`}>
        <Crown className={size === 'lg' ? 'w-7 h-7' : size === 'md' ? 'w-5 h-5' : 'w-4 h-4'} />
      </div>
    );
  }
  if (rank === 2) {
    return (
      <div className={`${sizeClasses[size]} rounded-full bg-gradient-to-br from-zinc-300 to-zinc-400 flex items-center justify-center font-bold text-black shadow-lg`}>
        {rank}
      </div>
    );
  }
  if (rank === 3) {
    return (
      <div className={`${sizeClasses[size]} rounded-full bg-gradient-to-br from-amber-600 to-amber-700 flex items-center justify-center font-bold text-white shadow-lg`}>
        {rank}
      </div>
    );
  }
  return (
    <div className={`${sizeClasses[size]} rounded-full bg-zinc-800 flex items-center justify-center font-bold text-zinc-400`}>
      {rank}
    </div>
  );
};

// Leaderboard entry row
const LeaderboardRow = ({ entry, rank, type, isCurrentUser }) => {
  const config = LEADERBOARD_TYPES[type];
  const isTopThree = rank <= 3;

  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: rank * 0.03 }}
      className={`flex items-center gap-4 p-4 rounded-xl transition-all ${
        isCurrentUser
          ? 'bg-violet-500/10 border border-violet-500/30'
          : isTopThree
          ? 'bg-zinc-800/50'
          : 'bg-zinc-900/30 hover:bg-zinc-800/30'
      }`}
    >
      {/* Rank */}
      <RankBadge rank={rank} size={isTopThree ? 'md' : 'sm'} />

      {/* User info */}
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${config.gradient} flex items-center justify-center shrink-0`}>
          <User className="w-5 h-5 text-white" />
        </div>
        <div className="min-w-0">
          <p className={`font-medium truncate ${isCurrentUser ? 'text-violet-400' : 'text-white'}`}>
            {entry.user_name || entry.user_id?.slice(0, 12) || 'Anonymous'}
            {isCurrentUser && <span className="ml-2 text-xs">(You)</span>}
          </p>
          <p className="text-xs text-zinc-500 truncate">
            {entry.user_id?.slice(0, 16)}...
          </p>
        </div>
      </div>

      {/* Stats */}
      <div className="flex items-center gap-6">
        {config.stats.map((stat) => {
          const Icon = stat.icon;
          const value = entry[stat.key];
          return (
            <div key={stat.key} className="text-center hidden sm:block">
              <p className={`text-lg font-bold text-${config.color}-400`}>
                {typeof value === 'number' ? (value % 1 === 0 ? value : value.toFixed(1)) : value || 0}
              </p>
              <p className="text-xs text-zinc-500 flex items-center gap-1">
                <Icon className="w-3 h-3" />
                {stat.label}
              </p>
            </div>
          );
        })}
      </div>
    </motion.div>
  );
};

// Top 3 podium display
const TopThreePodium = ({ entries, type }) => {
  const config = LEADERBOARD_TYPES[type];
  const [first, second, third] = entries;

  const PodiumCard = ({ entry, rank, height }) => {
    if (!entry) return <div className={`w-32 ${height}`} />;
    
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: rank * 0.1 }}
        className={`w-32 ${height} flex flex-col items-center justify-end`}
      >
        {/* Avatar */}
        <div className="relative mb-2">
          <div className={`w-16 h-16 rounded-full bg-gradient-to-br ${config.gradient} flex items-center justify-center`}>
            <User className="w-8 h-8 text-white" />
          </div>
          <div className="absolute -bottom-1 -right-1">
            <RankBadge rank={rank} size="sm" />
          </div>
        </div>
        
        {/* Name */}
        <p className="font-semibold text-white text-center text-sm truncate w-full px-1">
          {entry.user_name || 'Anonymous'}
        </p>
        
        {/* Main stat */}
        <p className={`text-${config.color}-400 font-bold text-lg`}>
          {entry[config.stats[0].key] || 0}
        </p>
        <p className="text-xs text-zinc-500">{config.stats[0].label}</p>
        
        {/* Podium base */}
        <div className={`w-full mt-2 rounded-t-lg bg-gradient-to-b ${
          rank === 1 ? 'from-amber-500/30 to-amber-500/10 h-20' :
          rank === 2 ? 'from-zinc-400/30 to-zinc-400/10 h-14' :
          'from-amber-700/30 to-amber-700/10 h-10'
        }`} />
      </motion.div>
    );
  };

  return (
    <div className="flex items-end justify-center gap-4 mb-8 py-8">
      <PodiumCard entry={second} rank={2} height="h-48" />
      <PodiumCard entry={first} rank={1} height="h-56" />
      <PodiumCard entry={third} rank={3} height="h-44" />
    </div>
  );
};

// User stats card
const UserStatsCard = ({ stats, userId }) => {
  if (!stats) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-gradient-to-br from-violet-500/10 to-indigo-500/10 border border-violet-500/30 rounded-2xl p-6 mb-8"
    >
      <div className="flex items-center gap-3 mb-4">
        <div className="w-12 h-12 rounded-full bg-violet-500/20 flex items-center justify-center">
          <Award className="w-6 h-6 text-violet-400" />
        </div>
        <div>
          <h3 className="font-semibold text-white">Your Stats</h3>
          <p className="text-xs text-zinc-500">Keep contributing to climb the ranks!</p>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div className="text-center p-3 bg-black/20 rounded-xl">
          <MessageSquare className="w-5 h-5 text-emerald-400 mx-auto mb-1" />
          <p className="text-xl font-bold text-white">{stats.reviews?.review_count || 0}</p>
          <p className="text-xs text-zinc-500">Reviews</p>
        </div>
        <div className="text-center p-3 bg-black/20 rounded-xl">
          <Upload className="w-5 h-5 text-amber-400 mx-auto mb-1" />
          <p className="text-xl font-bold text-white">{stats.presets?.presets_shared || 0}</p>
          <p className="text-xs text-zinc-500">Presets Shared</p>
        </div>
        <div className="text-center p-3 bg-black/20 rounded-xl">
          <Download className="w-5 h-5 text-violet-400 mx-auto mb-1" />
          <p className="text-xl font-bold text-white">{stats.presets?.total_downloads || 0}</p>
          <p className="text-xs text-zinc-500">Downloads</p>
        </div>
      </div>
    </motion.div>
  );
};

// Main LeaderboardPage component
const LeaderboardPage = () => {
  const { user } = useAuth();
  const [activeType, setActiveType] = useState('reviewers');
  const [entries, setEntries] = useState([]);
  const [userStats, setUserStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchLeaderboard = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/store/leaderboard?type=${activeType}&limit=20`);
      if (!response.ok) throw new Error('Failed to fetch leaderboard');
      
      const data = await response.json();
      setEntries(data.entries || []);
    } catch (error) {
      console.error('Error fetching leaderboard:', error);
    } finally {
      setLoading(false);
    }
  }, [activeType]);

  const fetchUserStats = useCallback(async () => {
    if (!user?.id) return;
    
    try {
      const response = await fetch(`${API_URL}/api/store/leaderboard/user/${user.id}`);
      if (!response.ok) return;
      
      const data = await response.json();
      setUserStats(data);
    } catch (error) {
      console.error('Error fetching user stats:', error);
    }
  }, [user?.id]);

  useEffect(() => {
    fetchLeaderboard();
  }, [fetchLeaderboard]);

  useEffect(() => {
    fetchUserStats();
  }, [fetchUserStats]);

  const config = LEADERBOARD_TYPES[activeType];
  const Icon = config.icon;

  return (
    <div className="min-h-screen bg-zinc-950 text-white" data-testid="leaderboard-page">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-zinc-950/80 backdrop-blur-xl border-b border-zinc-800">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/store">
                <Button variant="ghost" size="sm" className="text-zinc-400">
                  <ChevronLeft className="w-5 h-5 mr-1" />
                  Store
                </Button>
              </Link>
              <div>
                <h1 className="text-xl font-bold flex items-center gap-2">
                  <Trophy className="w-6 h-6 text-amber-400" />
                  Leaderboard
                </h1>
                <p className="text-xs text-zinc-500">Community rankings</p>
              </div>
            </div>
            <Link to="/">
              <img src="/images/string-sauce-logo.png" alt="String Sauce" className="h-8 w-auto" />
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Type selector tabs */}
        <div className="flex gap-2 mb-8 overflow-x-auto pb-2">
          {Object.entries(LEADERBOARD_TYPES).map(([key, typeConfig]) => {
            const TypeIcon = typeConfig.icon;
            const isActive = activeType === key;
            return (
              <button
                key={key}
                onClick={() => setActiveType(key)}
                className={`flex items-center gap-2 px-4 py-3 rounded-xl font-medium transition-all whitespace-nowrap ${
                  isActive
                    ? `bg-gradient-to-r ${typeConfig.gradient} text-white shadow-lg`
                    : 'bg-zinc-800/50 text-zinc-400 hover:bg-zinc-800'
                }`}
                data-testid={`tab-${key}`}
              >
                <TypeIcon className="w-5 h-5" />
                {typeConfig.title}
              </button>
            );
          })}
        </div>

        {/* Section header */}
        <div className="text-center mb-8">
          <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${config.gradient} flex items-center justify-center mx-auto mb-4`}>
            <Icon className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-2xl font-bold">{config.title}</h2>
          <p className="text-zinc-500">{config.subtitle}</p>
        </div>

        {/* User stats */}
        {user && userStats && (
          <UserStatsCard stats={userStats} userId={user.id} />
        )}

        {/* Loading state */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-violet-400" />
          </div>
        ) : entries.length > 0 ? (
          <>
            {/* Top 3 podium */}
            {entries.length >= 3 && (
              <TopThreePodium entries={entries.slice(0, 3)} type={activeType} />
            )}

            {/* Full leaderboard */}
            <div className="space-y-2">
              {entries.map((entry, index) => (
                <LeaderboardRow
                  key={entry.user_id || index}
                  entry={entry}
                  rank={entry.rank || index + 1}
                  type={activeType}
                  isCurrentUser={user?.id === entry.user_id}
                />
              ))}
            </div>
          </>
        ) : (
          <div className="text-center py-20">
            <Users className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-zinc-400 mb-2">No entries yet</h3>
            <p className="text-zinc-500 mb-4">Be the first to make the leaderboard!</p>
            <Link to="/marketplace">
              <Button className={`bg-gradient-to-r ${config.gradient}`}>
                Start Contributing
              </Button>
            </Link>
          </div>
        )}

        {/* How to climb */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mt-12 p-6 bg-zinc-900/50 border border-zinc-800 rounded-2xl"
        >
          <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-amber-400" />
            How to Climb the Ranks
          </h3>
          <div className="grid sm:grid-cols-3 gap-4">
            <div className="p-4 bg-zinc-800/50 rounded-xl">
              <MessageSquare className="w-6 h-6 text-emerald-400 mb-2" />
              <h4 className="font-medium text-white mb-1">Write Reviews</h4>
              <p className="text-sm text-zinc-500">Help others by reviewing presets you've used</p>
            </div>
            <div className="p-4 bg-zinc-800/50 rounded-xl">
              <Upload className="w-6 h-6 text-amber-400 mb-2" />
              <h4 className="font-medium text-white mb-1">Share Presets</h4>
              <p className="text-sm text-zinc-500">Create and share your own guitar presets</p>
            </div>
            <div className="p-4 bg-zinc-800/50 rounded-xl">
              <Star className="w-6 h-6 text-violet-400 mb-2" />
              <h4 className="font-medium text-white mb-1">Get Helpful Votes</h4>
              <p className="text-sm text-zinc-500">Quality contributions get recognized</p>
            </div>
          </div>
        </motion.div>
      </main>
    </div>
  );
};

export default LeaderboardPage;
